(() => {
  'use strict';

  const params = new URLSearchParams(window.location.search);
  if (!params.has('cbf_zone_builder')) return;

  const ORIGIN = [2.423264, 48.85574];
  const isoCache = new Map();
  let running = false;
  let downloadUrl = null;
  const DATA_URL = 'https://static.data.gouv.fr/resources/regions-departements-communes-et-codes-postaux-de-france-avec-un-point-gps-par-code-postal-et-lhistorique-des-changements/20261009-134931/cities.csv';
  const IDF_PREFIXES = new Set(['75','77','78','91','92','93','94','95']);
  const ZONES = [
    { label: '0–10 km', minimum: 30, cost: 10000 },
    { label: '10–20 km', minimum: 49, cost: 20000 },
    { label: '20–40 km', minimum: 69, cost: 40000 },
    { label: '40+ km', minimum: 89, cost: null }
  ];

  const aliases = {
    postal: ['postal_code','code_postal','zip_code','postcode'],
    lat: ['latitude','lat','gps_lat'],
    lon: ['longitude','lon','lng','gps_lng'],
    name: ['name','nom_commune','commune_name','city_name','nom'],
    validTo: ['valid_to','date_fin','end_date']
  };

  function norm(s) {
    return String(s || '').replace(/^\uFEFF/, '').trim().toLowerCase();
  }

  function detectDelimiter(line) {
    const counts = { ',': 0, ';': 0, '\t': 0 };
    let quoted = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') quoted = !quoted;
      else if (!quoted && Object.prototype.hasOwnProperty.call(counts, c)) counts[c]++;
    }
    return Object.entries(counts).sort((a,b) => b[1] - a[1])[0][0];
  }

  function parseCSV(text) {
    const firstLine = text.split(/\r?\n/, 1)[0] || '';
    const delimiter = detectDelimiter(firstLine);
    const rows = [];
    let row = [], cell = '', quoted = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (c === '"') {
        if (quoted && text[i + 1] === '"') { cell += '"'; i++; }
        else quoted = !quoted;
      } else if (c === delimiter && !quoted) {
        row.push(cell); cell = '';
      } else if ((c === '\n' || c === '\r') && !quoted) {
        if (c === '\r' && text[i + 1] === '\n') i++;
        row.push(cell); cell = '';
        if (row.some(v => String(v).trim() !== '')) rows.push(row);
        row = [];
      } else {
        cell += c;
      }
    }
    if (cell.length || row.length) { row.push(cell); rows.push(row); }
    if (rows.length < 2) throw new Error('CSV vide ou illisible');
    const headers = rows.shift().map(norm);
    return rows.map(values => {
      const obj = {};
      headers.forEach((h, i) => obj[h] = values[i] ?? '');
      return obj;
    });
  }

  function pickHeader(sample, list) {
    const keys = Object.keys(sample || {});
    return list.find(a => keys.includes(a)) || null;
  }

  function toNumber(v) {
    if (typeof v === 'number') return v;
    const n = Number(String(v ?? '').trim().replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
  }

  function normalizePostal(v) {
    const s = String(v ?? '').trim().replace(/\s+/g, '');
    return /^\d{5}$/.test(s) ? s : null;
  }

  function createUI() {
    const wrap = document.createElement('section');
    wrap.id = 'cbf-zone-builder';
    wrap.innerHTML = `
      <style>
        #cbf-zone-builder{max-width:1200px;margin:24px auto;padding:22px;border:2px solid #582863;border-radius:20px;background:#fff;box-shadow:0 10px 30px rgba(59,32,82,.12);font-family:inherit}
        #cbf-zone-builder h2{margin:0 0 8px;color:#3b2052;font-size:26px}
        #cbf-zone-builder p{margin:6px 0;color:#48404e}
        #cbf-zone-builder .cbfz-status{margin:16px 0;padding:14px 16px;border-radius:12px;background:#f4eff8;font-weight:700;color:#3b2052}
        #cbf-zone-builder .cbfz-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:16px 0}
        #cbf-zone-builder .cbfz-kpi{padding:14px;border-radius:14px;background:#f8f8f8;text-align:center}
        #cbf-zone-builder .cbfz-kpi b{display:block;font-size:24px;color:#3b2052}
        #cbf-zone-builder .cbfz-actions{display:flex;gap:10px;flex-wrap:wrap;margin:16px 0}
        #cbf-zone-builder button,#cbf-zone-builder a.cbfz-download{appearance:none;border:0;border-radius:999px;padding:11px 18px;background:#3b2052;color:white;font-weight:800;text-decoration:none;cursor:pointer}
        #cbf-zone-builder button.secondary{background:#b4f436;color:#26320a}
        #cbf-zone-builder table{width:100%;border-collapse:collapse;font-size:13px;margin-top:12px}
        #cbf-zone-builder th,#cbf-zone-builder td{padding:8px;border-bottom:1px solid #ece7ef;text-align:left}
        #cbf-zone-builder th{color:#3b2052}
        #cbf-zone-builder .cbfz-scroll{max-height:420px;overflow:auto;border:1px solid #ece7ef;border-radius:12px}
        #cbf-zone-builder .cbfz-warn{color:#9b4d00;font-weight:700}
        @media(max-width:760px){#cbf-zone-builder .cbfz-grid{grid-template-columns:1fr 1fr}}
      </style>
      <h2>Générateur zones Livraison Express</h2>
      <p>Référence fixe : <strong>47 rue des Deux Communes, Montreuil</strong> · réseau routier IGN BD TOPO · isodistance.</p>
      <p>Règle prudente : si un même code postal tombe dans plusieurs paliers, on garde le <strong>palier le plus bas</strong>.</p>
      <div class="cbfz-status" id="cbfz-status">Préparation…</div>
      <div class="cbfz-grid" id="cbfz-grid" hidden></div>
      <div class="cbfz-actions" id="cbfz-actions" hidden></div>
      <div id="cbfz-note"></div>
      <div class="cbfz-scroll" id="cbfz-table" hidden></div>
    `;
    const anchor = document.querySelector('main') || document.body;
    anchor.prepend(wrap);
    return wrap;
  }

  function setStatus(text) {
    const el = document.getElementById('cbfz-status');
    if (el) el.textContent = text;
  }

  function collectGeometries(node, out = [], seen = new Set()) {
    if (!node || typeof node !== 'object' || seen.has(node)) return out;
    seen.add(node);
    if (node.type === 'Polygon' || node.type === 'MultiPolygon') {
      out.push(node);
      return out;
    }
    if (node.type === 'Feature') return collectGeometries(node.geometry, out, seen);
    if (node.type === 'FeatureCollection' && Array.isArray(node.features)) {
      node.features.forEach(f => collectGeometries(f, out, seen));
      return out;
    }
    ['geometry','geometries','feature','features','isochrone','result','results'].forEach(k => {
      if (node[k]) collectGeometries(node[k], out, seen);
    });
    return out;
  }

  function inRing(point, ring) {
    let inside = false;
    const x = point[0], y = point[1];
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0], yi = ring[i][1];
      const xj = ring[j][0], yj = ring[j][1];
      const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / ((yj - yi) || Number.EPSILON) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }

  function inPolygon(point, coords) {
    if (!Array.isArray(coords) || !coords.length || !inRing(point, coords[0])) return false;
    for (let i = 1; i < coords.length; i++) if (inRing(point, coords[i])) return false;
    return true;
  }

  function pointInGeometry(point, geom) {
    if (geom.type === 'Polygon') return inPolygon(point, geom.coordinates);
    if (geom.type === 'MultiPolygon') return geom.coordinates.some(poly => inPolygon(point, poly));
    return false;
  }

  function pointInAny(point, geoms) {
    return geoms.some(g => pointInGeometry(point, g));
  }

  async function fetchIso(cost) {
    if (isoCache.has(cost)) return isoCache.get(cost);
    const u = new URL('https://data.geopf.fr/navigation/isochrone');
    u.searchParams.set('resource', 'bdtopo-valhalla');
    u.searchParams.set('point', ORIGIN.join(','));
    u.searchParams.set('direction', 'departure');
    u.searchParams.set('costType', 'distance');
    u.searchParams.set('costValue', String(cost));
    u.searchParams.set('profile', 'car');
    u.searchParams.set('distanceUnit', 'meter');
    u.searchParams.set('timeUnit', 'second');
    u.searchParams.set('crs', 'EPSG:4326');
    u.searchParams.set('geometryFormat', 'geojson');
    for (let attempt = 1; attempt <= 3; attempt++) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 45000);
      let retryable = true;
      try {
        const r = await fetch(u.toString(), {
          headers: { 'Accept': 'application/json' },
          cache: 'no-store',
          signal: controller.signal
        });
        retryable = r.status === 408 || r.status === 429 || r.status >= 500;
        if (!r.ok) throw new Error(`IGN ${cost/1000} km : HTTP ${r.status}`);
        retryable = false;
        const json = await r.json();
        const geoms = collectGeometries(json);
        if (!geoms.length) throw new Error(`IGN ${cost/1000} km : géométrie absente`);
        const result = { geoms, version: json.resourceVersion || json.resource_version || '' };
        isoCache.set(cost, result);
        return result;
      } catch (err) {
        if (!retryable || attempt === 3) {
          if (err.name === 'AbortError') throw new Error(`IGN ${cost/1000} km : délai dépassé après 3 tentatives`);
          throw err;
        }
        setStatus(`IGN ${cost/1000} km momentanément indisponible — nouvelle tentative ${attempt + 1}/3…`);
      } finally {
        clearTimeout(timer);
      }
      await new Promise(resolve => setTimeout(resolve, attempt * 2000));
    }
  }

  function csvEscape(v) {
    const s = String(v ?? '');
    return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }

  function render(results, versions) {
    const counts = [0,0,0,0];
    results.forEach(r => counts[r.zone]++);
    const grid = document.getElementById('cbfz-grid');
    grid.hidden = false;
    grid.innerHTML = ZONES.map((z,i) => `<div class="cbfz-kpi"><b>${counts[i]}</b><span>${z.label}<br>minimum ${z.minimum} €</span></div>`).join('');

    const mixed = results.filter(r => r.mixed);
    const note = document.getElementById('cbfz-note');
    note.innerHTML = `<p><strong>${results.length}</strong> codes postaux d’Île-de-France classés. <span class="cbfz-warn">${mixed.length} code(s) postal(aux) à cheval sur plusieurs paliers</span> : ils ont automatiquement reçu le palier inférieur.</p>${versions.filter(Boolean).length ? `<p>Version réseau IGN : ${[...new Set(versions.filter(Boolean))].join(', ')}</p>` : ''}`;

    const table = document.getElementById('cbfz-table');
    table.hidden = false;
    table.innerHTML = `<table><thead><tr><th>Code postal</th><th>Palier</th><th>Minimum</th><th>Points</th><th>Contrôle</th></tr></thead><tbody>${results.map(r => `<tr><td>${r.postal}</td><td>${ZONES[r.zone].label}</td><td>${ZONES[r.zone].minimum} €</td><td>${r.points}</td><td>${r.mixed ? '<span class="cbfz-warn">À cheval → palier inférieur</span>' : 'OK'}</td></tr>`).join('')}</tbody></table>`;

    const lines = ['code_postal;palier_reference;minimum_panier_eur;nb_points;cas_limite'];
    results.forEach(r => lines.push([r.postal,ZONES[r.zone].label,ZONES[r.zone].minimum,r.points,r.mixed ? 'OUI - palier inférieur retenu' : 'NON'].map(csvEscape).join(';')));
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const href = URL.createObjectURL(blob);
    downloadUrl = href;

    const actions = document.getElementById('cbfz-actions');
    actions.hidden = false;
    actions.innerHTML = `<a class="cbfz-download" href="${href}" download="zones-livraison-express-idf.csv">Télécharger le CSV Shopify</a><button class="secondary" type="button" id="cbfz-retry">Recalculer</button>`;
    document.getElementById('cbfz-retry').addEventListener('click', () => {
      isoCache.clear();
      run();
    });
  }

  async function run() {
    if (running) return;
    running = true;
    for (const id of ['cbfz-grid', 'cbfz-actions', 'cbfz-table']) {
      const el = document.getElementById(id);
      el.hidden = true;
      el.replaceChildren();
    }
    document.getElementById('cbfz-note').replaceChildren();
    delete window.CBF_ZONE_RESULTS;
    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
      downloadUrl = null;
    }
    try {
      setStatus('1/5 — Chargement des codes postaux et points GPS…');
      const response = await fetch(DATA_URL, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Base codes postaux : HTTP ${response.status}`);
      const text = await response.text();
      const rows = parseCSV(text);
      const sample = rows[0] || {};
      const postalKey = pickHeader(sample, aliases.postal);
      const latKey = pickHeader(sample, aliases.lat);
      const lonKey = pickHeader(sample, aliases.lon);
      const nameKey = pickHeader(sample, aliases.name);
      const validToKey = pickHeader(sample, aliases.validTo);
      if (!postalKey || !latKey || !lonKey) throw new Error(`Colonnes introuvables. Colonnes reçues : ${Object.keys(sample).join(', ')}`);

      const points = [];
      for (const row of rows) {
        if (validToKey) {
          const end = String(row[validToKey] ?? '').trim().toLowerCase();
          if (end && end !== 'null' && end !== 'none') continue;
        }
        const postal = normalizePostal(row[postalKey]);
        if (!postal || !IDF_PREFIXES.has(postal.slice(0,2))) continue;
        const lat = toNumber(row[latKey]);
        const lon = toNumber(row[lonKey]);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
        points.push({ postal, lat, lon, name: nameKey ? String(row[nameKey] || '').trim() : '' });
      }
      if (!points.length) throw new Error('Aucun point d’Île-de-France trouvé dans la base.');

      setStatus(`2/5 — ${points.length} points IDF trouvés. Calcul IGN de l’isodistance 10 km…`);
      const iso10 = await fetchIso(10000);
      setStatus('3/5 — Calcul IGN de l’isodistance 20 km…');
      const iso20 = await fetchIso(20000);
      setStatus('4/5 — Calcul IGN de l’isodistance 40 km…');
      const iso40 = await fetchIso(40000);

      setStatus('5/5 — Classement des codes postaux…');
      const grouped = new Map();
      for (const p of points) {
        const pt = [p.lon, p.lat];
        let zone = 3;
        if (pointInAny(pt, iso10.geoms)) zone = 0;
        else if (pointInAny(pt, iso20.geoms)) zone = 1;
        else if (pointInAny(pt, iso40.geoms)) zone = 2;
        if (!grouped.has(p.postal)) grouped.set(p.postal, { zones: [], names: new Set() });
        const g = grouped.get(p.postal);
        g.zones.push(zone);
        if (p.name) g.names.add(p.name);
      }

      const results = [...grouped.entries()].map(([postal, g]) => {
        const unique = [...new Set(g.zones)].sort((a,b) => a-b);
        return { postal, zone: unique[0], mixed: unique.length > 1, points: g.zones.length, names: [...g.names] };
      }).sort((a,b) => a.postal.localeCompare(b.postal, 'fr'));

      window.CBF_ZONE_RESULTS = results;
      render(results, [iso10.version, iso20.version, iso40.version]);
      setStatus(`Terminé — ${results.length} codes postaux classés. Rien n’a été modifié dans les réglages de livraison Shopify.`);
    } catch (err) {
      console.error('[CBF Zone Builder]', err);
      setStatus(`ERREUR — ${err && err.message ? err.message : String(err)}`);
      const actions = document.getElementById('cbfz-actions');
      actions.hidden = false;
      actions.innerHTML = '<button class="secondary" type="button" id="cbfz-retry">Réessayer</button>';
      document.getElementById('cbfz-retry').addEventListener('click', run);
    } finally {
      running = false;
    }
  }

  function init() {
    if (document.getElementById('cbf-zone-builder')) return;
    createUI();
    run();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();

